import type { IMovementRepository, IProductRepository, IWarehouseRepository } from '../../domain/interfaces';
import type { MovementListItemDTO } from '../dtos/inventory/MovementListItemDTO';

export type { MovementListItemDTO };

export interface FindMovementsUseCase {
  execute(filters?: {
    productId?: string;
    warehouseId?: string;
    type?: 'IN' | 'OUT' | 'TRANSFER' | 'ADJUSTMENT';
    startDate?: Date;
    endDate?: Date;
  }): Promise<MovementListItemDTO[]>;
}

export default class FindMovementsUseCaseImpl implements FindMovementsUseCase {
  constructor(
    private readonly movementRepository: IMovementRepository,
    private readonly productRepository: IProductRepository,
    private readonly warehouseRepository: IWarehouseRepository
  ) {}

  async execute(filters?: {
    productId?: string;
    warehouseId?: string;
    type?: 'IN' | 'OUT' | 'TRANSFER' | 'ADJUSTMENT';
    startDate?: Date;
    endDate?: Date;
  }): Promise<MovementListItemDTO[]> {
    try {
      const movementEntities = await this.movementRepository.findAll();
      let movements = movementEntities.map((m) => m.toJSON());

      if (filters?.productId) {
        movements = movements.filter((m) => m.productId === filters.productId);
      }

      if (filters?.warehouseId) {
        movements = movements.filter(
          (m) =>
            m.warehouseId === filters.warehouseId ||
            m.destinationWarehouseId === filters.warehouseId
        );
      }

      if (filters?.type) {
        movements = movements.filter((m) => m.movementType === filters.type);
      }

      if (filters?.startDate) {
        movements = movements.filter(
          (m) => new Date(m.createdAt) >= filters.startDate!
        );
      }

      if (filters?.endDate) {
        movements = movements.filter(
          (m) => new Date(m.createdAt) <= filters.endDate!
        );
      }

      movements.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      // Promise caches to avoid N+1 queries — store the promise itself so concurrent lookups reuse it
      const productCache = new Map<string, Promise<{ name: string; sku: string } | null>>();
      const warehouseCache = new Map<string, Promise<{ name: string } | null>>();

      const resolveProduct = (productId: string) => {
        if (!productCache.has(productId)) {
          productCache.set(
            productId,
            this.productRepository
              .findById(productId)
              .then((p) => (p ? { name: p.name, sku: p.sku } : null))
          );
        }
        return productCache.get(productId)!;
      };

      const resolveWarehouse = (warehouseId: string) => {
        if (!warehouseCache.has(warehouseId)) {
          warehouseCache.set(
            warehouseId,
            this.warehouseRepository
              .findById(warehouseId)
              .then((w) => (w ? { name: w.name } : null))
          );
        }
        return warehouseCache.get(warehouseId)!;
      };

      const result = await Promise.all(
        movements.map(async (m) => {
          const [product, warehouse, destinationWarehouse] = await Promise.all([
            resolveProduct(m.productId),
            resolveWarehouse(m.warehouseId),
            m.destinationWarehouseId ? resolveWarehouse(m.destinationWarehouseId) : Promise.resolve(null),
          ]);

          return {
            id: m._id,
            movementType: m.movementType as 'IN' | 'OUT' | 'TRANSFER' | 'ADJUSTMENT',
            productId: m.productId,
            productName: product?.name ?? 'Unknown Product',
            productSku: product?.sku ?? 'N/A',
            warehouseId: m.warehouseId,
            warehouseName: warehouse?.name,
            destinationWarehouseId: m.destinationWarehouseId,
            destinationWarehouseName: destinationWarehouse?.name,
            quantity: m.quantity.value,
            unit: m.quantity.unit,
            reference: m.reference,
            reason: m.reason,
            notes: m.notes,
            status: m.status,
            createdAt: m.createdAt,
          };
        })
      );

      return result;
    } catch (error) {
      throw new Error(
        `Failed to find movements: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }
}
